import React, { useState, useEffect } from "react";
import divider from "../../../assets/website/images/divider.png";
import TablerIcon from "../shared/tablerIcon";
import { postContactUs, CMSPageDetails } from "../services/website.services";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../utils/Constants";
import * as Yup from "yup";
import { useFormik } from "formik";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

function WebsiteContactPage() {
  const [loading, setLoading] = useState(false);
  // ✅ CMS dynamic data
  const [contactInfo, setContactInfo] = useState({
    email_support: "",
    call_us: "",
    office_address: "",
  });

  const [cmsLoading, setCmsLoading] = useState(true);

  const contactSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, "First name must be at least 2 characters")
      .required("First name is required"),

    last_name: Yup.string()
      .min(2, "Last name must be at least 2 characters")
      .required("Last name is required"),

    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),

    phone: Yup.string()
      .matches(/^[0-9+\-\s()]{7,15}$/, "Invalid phone number")
      .required("Phone number is required"),

    subject: Yup.string()
      .min(5, "Subject must be at least 5 characters")
      .required("Subject is required"),

    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
    validationSchema: contactSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      const payload = {
        name: `${values.first_name} ${values.last_name}`.trim(),
        email: values.email,
        subject: values.subject,
        message: values.message,
        phone: "+" + values.phone,
        type: "web",
      };

      try {
        const response = await postContactUs(payload);
        if (response) {
          Swal.fire({
            icon: "success",
            text: response.message || "Your query is submitted successfully",
            ...SWAL_SETTINGS,
          });
          resetForm();
        }
      } catch (error) {
        console.error("error during contact us submission", error);
      } finally {
        setLoading(false);
      }
    },
  });

  // =========================
  // ✅ FETCH CMS DATA
  // =========================
  useEffect(() => {
    const fetchCMSData = async () => {
      try {
        const [emailRes, callRes, officeRes] = await Promise.all([
          CMSPageDetails("email_support", "website"),
          CMSPageDetails("call_us", "website"),
          CMSPageDetails("office_address", "website"),
        ]);

        setContactInfo({
          email_support: emailRes?.data?.description || "",
          call_us: callRes?.data?.description || "",
          office_address: officeRes?.data?.description || "",
        });
      } catch (err) {
        console.error("CMS fetch error", err);
      } finally {
        setCmsLoading(false);
      }
    };

    fetchCMSData();
  }, []);

  return (
    <main id="home" className="px-0 lg:px-12">
      <section className="py-10 sm:py-14 lg:py-20 !pt-0">
        <div className="container">
          <div>
            {/* HEADER */}
            <div className="mb-8 rounded-2xl  bg-primary-50/40 px-4 py-8 text-center sm:mb-10 sm:px-8 sm:py-10">
              <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-primary">
                Support
              </div>

            
              <h1 className="text-3xl font-bold sm:text-4xl lg:text-5xl">
                Contact <span className="text-primary">Us</span>
              </h1>

              <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
                Have a question, feedback, or support request? Our team is here to help.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_minmax(0,1fr)]">
              
              {/* =========================
                  LEFT SIDE (DYNAMIC CMS)
              ========================= */}
              <aside className="flex gap-4 flex-col">
                <style>{`
                  .cms-contact-info h1, .cms-contact-info h2, .cms-contact-info h3 {
                    margin-bottom: 0.5rem !important;
                    font-weight: 700 !important;
                    color: #343434 !important; 
                  }
                  .cms-contact-info h1, .cms-contact-info h2 { font-size: 1.5rem !important; line-height: 2rem !important; }
                  .cms-contact-info h3 { font-size: 1.25rem !important; line-height: 1.75rem !important; }
                  .cms-contact-info p { margin-bottom: 0.25rem !important; }
                  .cms-contact-info p:last-child { margin-bottom: 0 !important; }
                  .contact-icon-wrapper { margin-bottom: 0.75rem; color: #4b5563; }
                  .react-tel-input .selected-flag { border-radius: 0.75rem 0 0 0.75rem !important; }
                `}</style>
                
                {/* EMAIL */}
                <article className="rounded-2xl bg-white border p-5 shadow">
                  <div className="contact-icon-wrapper">
                    <TablerIcon name="mail" size={24} />
                  </div>
                  {/* <h2 className="text-lg font-semibold">Email Support</h2> */}

                  {cmsLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <div
                      className="mt-2 text-sm text-muted-foreground cms-contact-info"
                      dangerouslySetInnerHTML={{
                        __html: contactInfo.email_support,
                      }}
                    />
                  )}
                </article>

                {/* PHONE */}
                <article className="rounded-2xl border bg-white p-5 shadow">
                  <div className="contact-icon-wrapper">
                    <TablerIcon name="phone" size={24} />
                  </div>
                  {/* <h2 className="text-lg font-semibold">Call Us</h2> */}

                  {cmsLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <div
                      className="mt-2 text-sm text-muted-foreground cms-contact-info"
                      dangerouslySetInnerHTML={{
                        __html: contactInfo.call_us,
                      }}
                    />
                  )}
                </article>

                {/* OFFICE */}
                <article className="rounded-2xl border bg-white p-5 shadow">
                  <div className="contact-icon-wrapper">
                    <TablerIcon name="map-pin" size={24} />
                  </div>
                  {/* <h2 className="text-lg font-semibold">Office</h2> */}

                  {cmsLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <div
                      className="mt-2 text-sm text-muted-foreground cms-contact-info"
                      dangerouslySetInnerHTML={{
                        __html: contactInfo.office_address,
                      }}
                    />
                  )}
                </article>

              </aside>

              {/* =========================
                  RIGHT SIDE (FORM)
              ========================= */}
              <article className="rounded-2xl border border-input bg-white p-5 shadow-[0px_3px_8px_-1px_rgba(0,0,0,0.08)] sm:p-7 lg:p-8">
                <h2 className="text-xl font-semibold text-black-fix-2 sm:text-2xl">Send us a message</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-base">
                  Fill out the form below and our team will get back to you as soon as possible.
                </p>

                <form className="mt-6 space-y-5" onSubmit={formik.handleSubmit}>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="first_name" className="mb-2 block text-sm font-medium text-black-fix-2">
                        First Name
                      </label>
                      <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter first name"
                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-black-fix-2 outline-none transition focus:border-primary ${
                          formik.touched.first_name && formik.errors.first_name ? "border-red-500" : "border-input"
                        }`}
                      />
                      {formik.touched.first_name && formik.errors.first_name && (
                        <div className="mt-1 text-sm text-red-500">{formik.errors.first_name}</div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="last_name" className="mb-2 block text-sm font-medium text-black-fix-2">
                        Last Name
                      </label>
                      <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter last name"
                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-black-fix-2 outline-none transition focus:border-primary ${
                          formik.touched.last_name && formik.errors.last_name ? "border-red-500" : "border-input"
                        }`}
                      />
                      {formik.touched.last_name && formik.errors.last_name && (
                        <div className="mt-1 text-sm text-red-500">{formik.errors.last_name}</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-black-fix-2">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="you@example.com"
                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-black-fix-2 outline-none transition focus:border-primary ${
                          formik.touched.email && formik.errors.email ? "border-red-500" : "border-input"
                        }`}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="mt-1 text-sm text-red-500">{formik.errors.email}</div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="phone" className="mb-2 block text-sm font-medium text-black-fix-2">
                        Phone Number
                      </label>
                      <PhoneInput
                        country={'us'}
                        value={formik.values.phone}
                        onChange={(phone) => formik.setFieldValue('phone', phone)}
                        onBlur={() => formik.setFieldTouched('phone', true)}
                        inputProps={{
                          name: 'phone',
                          id: 'phone'
                        }}
                        containerClass={`!w-full !rounded-xl !border bg-white !transition focus-within:!border-primary ${
                          formik.touched.phone && formik.errors.phone ? "!border-red-500" : "!border-input"
                        }`}
                        inputClass="!w-full !border-none !bg-transparent !py-3 !text-sm !text-black-fix-2 !outline-none focus:!shadow-none focus:!ring-0"
                        inputStyle={{ width: '100%', height: '46px' }}
                        buttonClass="!border-none !bg-transparent hover:!bg-transparent"
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <div className="mt-1 text-sm text-red-500">{formik.errors.phone}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="mb-2 block text-sm font-medium text-black-fix-2">
                      Subject
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="How can we help you?"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-black-fix-2 outline-none transition focus:border-primary ${
                        formik.touched.subject && formik.errors.subject ? "border-red-500" : "border-input"
                      }`}
                    />
                    {formik.touched.subject && formik.errors.subject && (
                      <div className="mt-1 text-sm text-red-500">{formik.errors.subject}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-black-fix-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Write your message here..."
                      className={`w-full resize-none rounded-xl border bg-white px-4 py-3 text-sm text-black-fix-2 outline-none transition focus:border-primary ${
                        formik.touched.message && formik.errors.message ? "border-red-500" : "border-input"
                      }`}
                    />
                    {formik.touched.message && formik.errors.message && (
                      <div className="mt-1 text-sm text-red-500">{formik.errors.message}</div>
                    )}
                  </div>

                  <button type="submit" disabled={loading} className="btn btn-primary inline-flex">
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </article>

            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default WebsiteContactPage;